import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { homeData } from "../../data/homeData";
import { assetPath } from "../../utils/assetPath";

const PlaceholderImage = ({ text }) => (
  <div className="w-full h-full bg-slate-100 flex items-center justify-center text-slate-400 font-bold tracking-widest uppercase">
    <span>{text}</span>
  </div>
);

const IndustryCard = ({ industry, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="flex flex-col group cursor-pointer"
    >
      {/* Top Image */}
      <div className="w-full h-[280px] sm:h-[350px] md:h-[500px] mb-6 overflow-hidden rounded-xl bg-slate-50 relative">
        {industry.image ? (
          <img
            src={assetPath(industry.image)}
            alt={industry.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
        ) : null}
        <PlaceholderImage
          text={industry.title}
          className={industry.image ? "hidden" : "flex"}
        />
      </div>

      {/* Content */}
      <h3 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight group-hover:text-blue-600 transition-colors">
        {industry.title}
      </h3>
      {industry.description && (
        <p className="text-slate-600 text-sm leading-relaxed mb-6 grow">
          {industry.description}
        </p>
      )}
      {industry.items && Array.isArray(industry.items) && (
        <ul className="space-y-3 mb-6 grow">
          {industry.items.map((item, j) => (
            <li key={j} className="flex items-start gap-2.5">
              <span className="text-blue-500 mt-1 text-[10px]">●</span>
              <span className="text-slate-700 text-[13.5px] font-medium leading-relaxed">
                {item}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* CTA Link */}
      <Link
        to={industry.path}
        className="inline-flex items-center text-sm font-bold w-fit border-b-2 border-transparent hover:border-current transition-all group-hover:gap-3 gap-2 pb-0.5"
        style={{ color: industry.color || "#3b82f6" }}
      >
        {industry.cta}{" "}
        <span className="text-lg leading-none transition-transform group-hover:translate-x-1">
          →
        </span>
      </Link>
    </motion.div>
  );
};

const IndustriesWeServe = ({ data }) => {
  const industriesWeServe = data || homeData.industriesWeServe;

  return (
    <section className="py-24 md:py-32 bg-white relative z-20">
      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Header Block */}
        <div className="text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-800 tracking-tight leading-[1.1] max-w-4xl text-balance mb-6">
              {industriesWeServe.heading}
            </h2>
            <p className="text-lg md:text-xl text-slate-600 font-medium max-w-2xl text-balance">
              {industriesWeServe.description}
            </p>
          </motion.div>
        </div>

        {/* The 3 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 lg:gap-16">
          {industriesWeServe.items.map((industry, index) => (
            <IndustryCard
              key={industry.title}
              industry={industry}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustriesWeServe;
